import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppShell } from '../../features/ui-system';
import axios from 'axios';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Eye,
  Filter,
  RefreshCw,
  Timer,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';
import {
  DataTableGrid,
  DataTableShell,
  DataTableState,
  FormField,
  FormSection,
  mapApplicationStatusToTone,
  MetricGrid,
  PageContainer,
  PageHeader,
  ReviewCard,
  SearchInput,
  SectionContainer,
  SplitGrid,
  StatCard,
  StatusBadge,
  StickyActionBar,
  TopTabs,
  UIButton,
} from '../../features/ui-system';

const API = `${resolveBackendBaseUrl()}/api`;
const PAGE_SIZE = 10;

const STATUS_OPTIONS = [
  { value: 'all', label: 'Semua' },
  { value: 'submitted', label: 'Dikirim' },
  { value: 'under_review', label: 'Direview' },
  { value: 'needs_revision', label: 'Perlu Revisi' },
  { value: 'approved', label: 'Disetujui' },
  { value: 'rejected', label: 'Ditolak' },
];

const URGENCY_OPTIONS = [
  { value: 'all', label: 'Semua Prioritas' },
  { value: 'high', label: 'Tinggi' },
  { value: 'medium', label: 'Menengah' },
  { value: 'low', label: 'Rendah' },
];

const DEFAULT_ADVANCED_FILTERS = {
  technician: 'all',
  site: 'all',
  packageName: 'all',
  status: 'all',
  urgency: 'all',
  dateFrom: '',
  dateTo: '',
};

const STATUS_LABEL = {
  submitted: 'Dikirim',
  under_review: 'Direview',
  needs_revision: 'Perlu Revisi',
  approved: 'Disetujui',
  rejected: 'Ditolak',
};

const STATUS_PRIORITY = {
  needs_revision: 4,
  under_review: 3,
  submitted: 2,
  rejected: 1,
  approved: 0,
};

const TABLE_COLUMNS = [
  { key: 'request', label: 'Request' },
  { key: 'customer', label: 'Pelanggan' },
  { key: 'package', label: 'Paket' },
  { key: 'photo', label: 'Foto' },
  { key: 'technician', label: 'Teknisi' },
  { key: 'status', label: 'Status' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'action', label: 'Aksi', align: 'right' },
];

const formatDateTime = (isoValue) => {
  const date = new Date(isoValue || '');
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatNumber = (value) => Number(value || 0).toLocaleString('id-ID');

const normalizeStatus = (value) => String(value || '').trim().toLowerCase();

const parseTimestamp = (value) => {
  const date = new Date(value || '');
  const epoch = date.getTime();
  return Number.isFinite(epoch) ? epoch : 0;
};

const getInitials = (name) => {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean).slice(0, 2);
  if (!words.length) return 'NA';
  return words.map((word) => word[0]?.toUpperCase() || '').join('');
};

const statusLabel = (status) => STATUS_LABEL[normalizeStatus(status)] || String(status || 'Unknown').replace(/_/g, ' ');

const resolveTechnicianValue = (item) => String(item?.technician_name || item?.technician_id || '').trim() || 'Tanpa Teknisi';
const resolveSiteValue = (item) => String(item?.site_name || item?.site_id || item?.area_name || item?.area || '').trim() || 'Tanpa Site';
const resolvePackageValue = (item) => String(item?.package_name || item?.package_id || '').trim() || 'Tanpa Paket';

const resolveUrgency = (item) => {
  const status = normalizeStatus(item?.status);
  if (status === 'needs_revision' || status === 'rejected') return 'high';
  if (status === 'submitted' || status === 'under_review') return 'medium';
  return 'low';
};

const getActiveFilterCount = (filters) => Object.entries(filters).filter(([key, value]) => {
  if (key === 'dateFrom' || key === 'dateTo') return Boolean(value);
  return value !== 'all';
}).length;

const toDecisionChecklist = (item) => {
  const hasKtp = Boolean(item?.photo_ktp_url || item?.photos_json?.[0]);
  const hasHouse = Boolean(item?.photo_house_url || item?.photos_json?.[1]);
  const hasTechnician = Boolean(String(item?.technician_name || item?.technician_id || '').trim());
  const hasPackage = Boolean(String(item?.package_name || item?.package_id || '').trim());
  const hasAddress = Boolean(String(item?.address || '').trim());
  return [
    { key: 'ktp', label: 'Foto KTP', ready: hasKtp },
    { key: 'house', label: 'Foto Rumah', ready: hasHouse },
    { key: 'technician', label: 'Assignment Teknisi', ready: hasTechnician },
    { key: 'package', label: 'Paket Layanan', ready: hasPackage },
    { key: 'address', label: 'Alamat Instalasi', ready: hasAddress },
  ];
};

const getTrendSeries = (rows = []) => {
  const days = Array.from({ length: 7 }).map((_, index) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - index));
    return {
      key: date.toISOString().slice(0, 10),
      label: date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short' }),
      value: 0,
    };
  });

  const dayMap = new Map(days.map((day) => [day.key, day]));
  rows.forEach((row) => {
    const timestamp = row?.submitted_at || row?.created_at;
    const key = String(timestamp || '').slice(0, 10);
    if (dayMap.has(key)) {
      dayMap.get(key).value += 1;
    }
  });

  return days;
};

export default function AdminCustomerRegistrations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submittingAction, setSubmittingAction] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(DEFAULT_ADVANCED_FILTERS);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_ADVANCED_FILTERS);

  const headers = useMemo(() => {
    const token = localStorage.getItem('ktc_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (status !== 'all') params.status = status;
      if (search.trim()) params.search = search.trim();
      const response = await axios.get(`${API}/customer-registration-requests`, { headers, params });
      setItems(Array.isArray(response?.data?.items) ? response.data.items : []);
    } catch (error) {
      console.error(error);
      toast.error('Gagal memuat pengajuan pelanggan');
    } finally {
      setLoading(false);
    }
  }, [headers, search, status]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    setCurrentPage(1);
  }, [status, search, advancedFilters]);

  useEffect(() => {
    setDraftFilters((prev) => ({ ...prev, status }));
  }, [status]);

  useEffect(() => {
    setAdvancedFilters((prev) => ({ ...prev, status }));
  }, [status]);

  const summary = useMemo(() => {
    const base = { total: items.length, waiting: 0, revision: 0, approved: 0, rejected: 0 };
    items.forEach((item) => {
      const itemStatus = normalizeStatus(item?.status);
      if (itemStatus === 'submitted' || itemStatus === 'under_review') base.waiting += 1;
      if (itemStatus === 'needs_revision') base.revision += 1;
      if (itemStatus === 'approved') base.approved += 1;
      if (itemStatus === 'rejected') base.rejected += 1;
    });
    return base;
  }, [items]);

  const technicianOptions = useMemo(() => (
    ['all', ...Array.from(new Set(items.map(resolveTechnicianValue))).sort((a, b) => a.localeCompare(b))]
  ), [items]);
  const siteOptions = useMemo(() => (
    ['all', ...Array.from(new Set(items.map(resolveSiteValue))).sort((a, b) => a.localeCompare(b))]
  ), [items]);
  const packageOptions = useMemo(() => (
    ['all', ...Array.from(new Set(items.map(resolvePackageValue))).sort((a, b) => a.localeCompare(b))]
  ), [items]);

  const sortedItems = useMemo(() => {
    const prepared = [...items].sort((a, b) => {
      const aStatus = normalizeStatus(a?.status);
      const bStatus = normalizeStatus(b?.status);
      const priorityDiff = (STATUS_PRIORITY[bStatus] || 0) - (STATUS_PRIORITY[aStatus] || 0);
      if (priorityDiff !== 0) return priorityDiff;
      return parseTimestamp(b?.submitted_at || b?.created_at) - parseTimestamp(a?.submitted_at || a?.created_at);
    });

    return prepared.filter((item) => {
      if (advancedFilters.technician !== 'all' && resolveTechnicianValue(item) !== advancedFilters.technician) return false;
      if (advancedFilters.site !== 'all' && resolveSiteValue(item) !== advancedFilters.site) return false;
      if (advancedFilters.packageName !== 'all' && resolvePackageValue(item) !== advancedFilters.packageName) return false;
      if (advancedFilters.status !== 'all' && normalizeStatus(item?.status) !== advancedFilters.status) return false;
      if (advancedFilters.urgency !== 'all' && resolveUrgency(item) !== advancedFilters.urgency) return false;

      const submittedAt = parseTimestamp(item?.submitted_at || item?.created_at);
      if (advancedFilters.dateFrom) {
        const from = parseTimestamp(`${advancedFilters.dateFrom}T00:00:00`);
        if (submittedAt < from) return false;
      }
      if (advancedFilters.dateTo) {
        const to = parseTimestamp(`${advancedFilters.dateTo}T23:59:59`);
        if (submittedAt > to) return false;
      }
      return true;
    });
  }, [advancedFilters, items]);

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pagedItems = useMemo(() => {
    const start = (safeCurrentPage - 1) * PAGE_SIZE;
    return sortedItems.slice(start, start + PAGE_SIZE);
  }, [safeCurrentPage, sortedItems]);

  const queueItems = useMemo(() => ({
    revision: sortedItems.filter((item) => normalizeStatus(item?.status) === 'needs_revision').slice(0, 5),
    pending: sortedItems.filter((item) => {
      const value = normalizeStatus(item?.status);
      return value === 'submitted' || value === 'under_review';
    }).slice(0, 5),
    urgent: sortedItems.filter((item) => {
      const value = normalizeStatus(item?.status);
      return value === 'needs_revision' || value === 'rejected';
    }).slice(0, 5),
  }), [sortedItems]);

  const trendSeries = useMemo(() => getTrendSeries(sortedItems), [sortedItems]);
  const activeFilterCount = useMemo(() => getActiveFilterCount(advancedFilters), [advancedFilters]);

  const tacticalSummary = useMemo(() => {
    const reviewed = items.filter((item) => ['approved', 'rejected', 'needs_revision'].includes(normalizeStatus(item?.status)));
    const reviewDurations = reviewed
      .map((item) => {
        const start = parseTimestamp(item?.submitted_at || item?.created_at);
        const end = parseTimestamp(item?.reviewed_at || item?.updated_at);
        if (!start || !end || end <= start) return null;
        return (end - start) / 60000;
      })
      .filter((value) => Number.isFinite(value) && value > 0);

    const averageMinutes = reviewDurations.length
      ? `${(reviewDurations.reduce((sum, value) => sum + value, 0) / reviewDurations.length).toFixed(1)}m`
      : '-';
    const rejectRate = items.length ? `${((summary.rejected / items.length) * 100).toFixed(1)}%` : '0%';
    const autoApproved = items.length
      ? `${((items.filter((item) => normalizeStatus(item?.status) === 'approved' && !String(item?.review_notes || '').trim()).length / items.length) * 100).toFixed(1)}%`
      : '0%';

    return {
      averageMinutes,
      rejectRate,
      autoApproved,
      queuePressure: summary.waiting + summary.revision,
    };
  }, [items, summary.rejected, summary.revision, summary.waiting]);

  const doReview = async (action) => {
    if (!selected?.id) return;
    setSubmittingAction(action);
    try {
      await axios.post(
        `${API}/customer-registration-requests/${selected.id}/review`,
        { action, review_notes: reviewNotes.trim() || undefined },
        { headers },
      );
      toast.success('Status pengajuan berhasil diperbarui');
      setSelected(null);
      setReviewNotes('');
      await loadRequests();
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Gagal memproses review';
      toast.error(detail);
    } finally {
      setSubmittingAction('');
    }
  };

  const openFilterDrawer = () => {
    setDraftFilters(advancedFilters);
    setIsFilterDrawerOpen(true);
  };

  const applyAdvancedFilters = () => {
    setAdvancedFilters(draftFilters);
    if (draftFilters.status !== status) {
      setStatus(draftFilters.status);
    }
    setIsFilterDrawerOpen(false);
  };

  const resetAdvancedFilters = () => {
    setDraftFilters(DEFAULT_ADVANCED_FILTERS);
    setAdvancedFilters(DEFAULT_ADVANCED_FILTERS);
    if (status !== 'all') setStatus('all');
  };

  const tabItems = STATUS_OPTIONS.map((option) => {
    if (option.value === 'all') return option;
    const count = items.filter((item) => normalizeStatus(item?.status) === option.value).length;
    return { ...option, count };
  });

  const rowsMeta = `${sortedItems.length ? (safeCurrentPage - 1) * PAGE_SIZE + 1 : 0}-${Math.min(safeCurrentPage * PAGE_SIZE, sortedItems.length)} dari ${sortedItems.length}`;

  return (
    <AppShell>
      <PageContainer className="pb-10">
      <PageHeader
        eyebrow="Validation Command Center"
        title="Review Pengajuan Pelanggan"
        subtitle="Validasi pengajuan layanan pelanggan dengan prioritas operasional dan eskalasi cepat."
        action={(
          <UIButton variant="ghost" onClick={loadRequests} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Muat Ulang
          </UIButton>
        )}
      />

      <MetricGrid>
        <StatCard title="Total Pengajuan" value={formatNumber(summary.total)} note="Seluruh request masuk" tone="info" trend="+12% dari periode lalu" />
        <StatCard title="Menunggu Review" value={formatNumber(summary.waiting)} note="Antrian validasi aktif" tone={summary.waiting > 0 ? 'warn' : 'ok'} />
        <StatCard title="Perlu Revisi" value={formatNumber(summary.revision)} note="Perlu follow-up teknisi" tone={summary.revision > 0 ? 'danger' : 'ok'} />
        <StatCard title="Disetujui" value={formatNumber(summary.approved)} note="Siap provisioning" tone="ok" />
      </MetricGrid>

      <SectionContainer>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <TopTabs tabs={tabItems} active={status} onChange={setStatus} variant="pills" />
          <div className="flex w-full items-center gap-2 xl:w-auto">
            <SearchInput
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Cari nama, request code, telepon, PPPoE..."
              className="max-w-[360px]"
            />
            <UIButton variant={activeFilterCount > 0 ? 'primary' : 'subtle'} onClick={openFilterDrawer}>
              <Filter className="h-4 w-4" />
              Filter Lanjut{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </UIButton>
          </div>
        </div>
      </SectionContainer>

      <SplitGrid>
        <DataTableShell
          title="Queue Pengajuan"
          subtitle="Permintaan prioritas ditampilkan di atas agar proses review lebih cepat."
          footer={(
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="cc-text-muted text-xs">Menampilkan {rowsMeta} entri</p>
              <div className="flex items-center gap-1">
                <UIButton
                  variant="subtle"
                  className="px-2.5 py-1.5 text-xs"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={safeCurrentPage <= 1}
                >
                  Prev
                </UIButton>
                {Array.from({ length: totalPages }).slice(0, 5).map((_, idx) => {
                  const page = idx + 1;
                  return (
                    <UIButton
                      key={`page-${page}`}
                      variant={page === safeCurrentPage ? 'primary' : 'subtle'}
                      className="px-2.5 py-1.5 text-xs"
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </UIButton>
                  );
                })}
                <UIButton
                  variant="subtle"
                  className="px-2.5 py-1.5 text-xs"
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={safeCurrentPage >= totalPages}
                >
                  Next
                </UIButton>
              </div>
            </div>
          )}
        >
          {loading ? (
            <DataTableState state="loading" message="Memuat daftar pengajuan..." />
          ) : null}
          {!loading && sortedItems.length === 0 ? (
            <DataTableState state="empty" message="Belum ada pengajuan pelanggan." />
          ) : null}
          {!loading && sortedItems.length > 0 ? (
            <DataTableGrid
              columns={TABLE_COLUMNS}
              rows={pagedItems}
              rowKey="id"
              getRowClassName={(row) => {
                const value = normalizeStatus(row?.status);
                if (value === 'needs_revision') return 'bg-rose-500/5 hover:bg-rose-500/10';
                if (value === 'under_review') return 'bg-amber-500/5 hover:bg-amber-500/10';
                return '';
              }}
              renderCell={(row, column) => {
                const value = normalizeStatus(row?.status);
                const hasKtp = Boolean(row?.photo_ktp_url || row?.photos_json?.[0]);
                const hasHouse = Boolean(row?.photo_house_url || row?.photos_json?.[1]);
                const packageName = resolvePackageValue(row);

                if (column.key === 'request') {
                  return (
                    <div>
                      <p className="text-sm font-semibold text-[#6bf8ac]">#{String(row?.request_code || '-')}</p>
                      <p className="cc-text-subtle text-[11px] uppercase tracking-[0.14em]">{String(row?.service_type || 'ftth')}</p>
                    </div>
                  );
                }
                if (column.key === 'customer') {
                  return (
                    <div>
                      <p className="text-sm font-medium text-slate-100">{String(row?.full_name || '-')}</p>
                      <p className="cc-text-muted text-xs">{String(row?.address || '-')}</p>
                      <p className="cc-text-subtle text-[11px]">{String(row?.phone || '-')}</p>
                    </div>
                  );
                }
                if (column.key === 'package') {
                  return <span className="inline-flex rounded-md bg-emerald-500/15 px-2 py-1 text-xs font-semibold text-emerald-200">{packageName}</span>;
                }
                if (column.key === 'photo') {
                  return (
                    <div className="flex flex-wrap gap-1">
                      <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${hasKtp ? 'bg-cyan-500/15 text-cyan-200' : 'bg-slate-700/50 text-slate-400'}`}>KTP</span>
                      <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${hasHouse ? 'bg-cyan-500/15 text-cyan-200' : 'bg-slate-700/50 text-slate-400'}`}>Rumah</span>
                    </div>
                  );
                }
                if (column.key === 'technician') {
                  return (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-800 text-[10px] font-semibold text-slate-300">
                        {getInitials(resolveTechnicianValue(row))}
                      </span>
                      <span className="text-xs text-slate-200">{resolveTechnicianValue(row)}</span>
                    </div>
                  );
                }
                if (column.key === 'status') {
                  return <StatusBadge tone={mapApplicationStatusToTone(value)}>{statusLabel(value)}</StatusBadge>;
                }
                if (column.key === 'submitted') {
                  return <p className="cc-text-secondary text-xs">{formatDateTime(row?.submitted_at || row?.created_at)}</p>;
                }
                if (column.key === 'action') {
                  return (
                    <div className="flex justify-end">
                      <UIButton
                        variant="subtle"
                        className="px-2.5 py-1.5 text-xs"
                        onClick={() => {
                          setSelected(row);
                          setReviewNotes(String(row?.review_notes || ''));
                        }}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Review
                      </UIButton>
                    </div>
                  );
                }
                return row?.[column.key] || '-';
              }}
            />
          ) : null}
        </DataTableShell>

        <div className="space-y-4">
          <SectionContainer>
            <div className="mb-3 flex items-center justify-between">
              <p className="cc-text-primary text-sm font-semibold">Action Queue</p>
              <span className="cc-chip cc-chip-warn">{queueItems.revision.length + queueItems.pending.length} aktif</span>
            </div>
            <div className="space-y-3">
              <div className="rounded-xl bg-[#0c1524]/80 p-3">
                <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-rose-300">Perlu Revisi</p>
                {queueItems.revision.length ? queueItems.revision.map((item) => (
                  <button
                    key={`revision-${item.id}`}
                    type="button"
                    onClick={() => {
                      setSelected(item);
                      setReviewNotes(String(item?.review_notes || ''));
                    }}
                    className="mb-1.5 w-full rounded-lg bg-rose-500/10 px-2.5 py-2 text-left last:mb-0 hover:bg-rose-500/20"
                  >
                    <p className="text-xs font-semibold text-rose-100">#{item?.request_code} - {item?.full_name}</p>
                    <p className="text-[11px] text-rose-200/80">{formatDateTime(item?.submitted_at || item?.created_at)}</p>
                  </button>
                )) : <p className="cc-text-muted text-xs">Tidak ada revisi aktif.</p>}
              </div>
              <div className="rounded-xl bg-[#0c1524]/80 p-3">
                <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-amber-300">Pending Validation</p>
                {queueItems.pending.length ? queueItems.pending.map((item) => (
                  <button
                    key={`pending-${item.id}`}
                    type="button"
                    onClick={() => {
                      setSelected(item);
                      setReviewNotes(String(item?.review_notes || ''));
                    }}
                    className="mb-1.5 w-full rounded-lg bg-amber-500/10 px-2.5 py-2 text-left last:mb-0 hover:bg-amber-500/20"
                  >
                    <p className="text-xs font-semibold text-amber-100">#{item?.request_code} - {item?.full_name}</p>
                    <p className="text-[11px] text-amber-200/80">{statusLabel(item?.status)}</p>
                  </button>
                )) : <p className="cc-text-muted text-xs">Antrian pending kosong.</p>}
              </div>
              <div className="rounded-xl bg-[#0c1524]/80 p-3">
                <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-cyan-300">High Priority</p>
                {queueItems.urgent.length ? queueItems.urgent.map((item) => (
                  <button
                    key={`urgent-${item.id}`}
                    type="button"
                    onClick={() => {
                      setSelected(item);
                      setReviewNotes(String(item?.review_notes || ''));
                    }}
                    className="mb-1.5 w-full rounded-lg bg-cyan-500/10 px-2.5 py-2 text-left last:mb-0 hover:bg-cyan-500/20"
                  >
                    <p className="text-xs font-semibold text-cyan-100">#{item?.request_code} - {item?.full_name}</p>
                    <p className="text-[11px] text-cyan-200/80">Butuh keputusan admin</p>
                  </button>
                )) : <p className="cc-text-muted text-xs">Tidak ada item prioritas tinggi.</p>}
              </div>
            </div>
          </SectionContainer>

          <SectionContainer>
            <p className="cc-text-primary mb-3 text-sm font-semibold">Tactical Summary</p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between rounded-lg bg-[#0c1524]/75 px-3 py-2">
                <span className="cc-text-secondary inline-flex items-center gap-1.5 text-xs"><Timer className="h-3.5 w-3.5" />Avg. Validation Time</span>
                <span className="font-semibold text-slate-100">{tacticalSummary.averageMinutes}</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-[#0c1524]/75 px-3 py-2">
                <span className="cc-text-secondary inline-flex items-center gap-1.5 text-xs"><AlertTriangle className="h-3.5 w-3.5" />Reject Rate</span>
                <span className="font-semibold text-rose-200">{tacticalSummary.rejectRate}</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-[#0c1524]/75 px-3 py-2">
                <span className="cc-text-secondary inline-flex items-center gap-1.5 text-xs"><CheckCircle2 className="h-3.5 w-3.5" />Auto-Approved</span>
                <span className="font-semibold text-emerald-200">{tacticalSummary.autoApproved}</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-[#0c1524]/75 px-3 py-2">
                <span className="cc-text-secondary inline-flex items-center gap-1.5 text-xs"><ClipboardList className="h-3.5 w-3.5" />Queue Pressure</span>
                <span className="font-semibold text-slate-100">{formatNumber(tacticalSummary.queuePressure)}</span>
              </div>
            </div>
            <UIButton variant="ghost" className="mt-3 w-full justify-center text-xs">
              Download Full Report
            </UIButton>
          </SectionContainer>
        </div>
      </SplitGrid>

      <SectionContainer>
        <p className="cc-text-primary mb-3 text-sm font-semibold">Submission Trends</p>
        <div className="grid grid-cols-7 gap-2">
          {trendSeries.map((item) => {
            const maxValue = Math.max(...trendSeries.map((series) => series.value), 1);
            const height = Math.max(10, Math.round((item.value / maxValue) * 90));
            return (
              <div key={item.key} className="flex flex-col items-center gap-1">
                <div className="flex h-28 w-full max-w-[58px] items-end rounded-md bg-[#0f1a2a]/80 px-2 py-1">
                  <div className="w-full rounded-sm bg-gradient-to-t from-emerald-500/70 to-emerald-300/95" style={{ height: `${height}%` }} />
                </div>
                <span className="cc-text-subtle text-[10px] uppercase">{item.label}</span>
              </div>
            );
          })}
        </div>
      </SectionContainer>

      {isFilterDrawerOpen ? (
        <div className="fixed inset-0 z-[88] flex justify-end bg-black/60">
          <aside className="h-full w-full max-w-md overflow-y-auto bg-[#0b1422]/95 p-4 shadow-[0_0_40px_rgba(0,0,0,0.5)] backdrop-blur">
            <div className="mb-4 flex items-start justify-between gap-2">
              <div>
                <p className="cc-text-subtle text-[10px] font-semibold uppercase tracking-[0.18em]">Advanced Filters</p>
                <h3 className="cc-text-primary text-lg font-semibold">Filter Pengajuan</h3>
                <p className="cc-text-secondary text-xs">Saring data tanpa memenuhi command bar utama.</p>
              </div>
              <UIButton variant="subtle" className="px-2 py-2" onClick={() => setIsFilterDrawerOpen(false)}>
                <XCircle className="h-4 w-4" />
              </UIButton>
            </div>

            <FormSection title="Filter Operasional" description="Teknisi, area, paket, status, tanggal submit, dan urgensi.">
              <div className="space-y-3">
                <FormField label="Teknisi">
                  <select
                    value={draftFilters.technician}
                    onChange={(event) => setDraftFilters((prev) => ({ ...prev, technician: event.target.value }))}
                    className="cc-input-shell cc-input-field h-10 w-full rounded-xl px-3 text-sm outline-none"
                  >
                    {technicianOptions.map((option) => (
                      <option key={`tech-${option}`} value={option}>{option === 'all' ? 'Semua Teknisi' : option}</option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Site / Area">
                  <select
                    value={draftFilters.site}
                    onChange={(event) => setDraftFilters((prev) => ({ ...prev, site: event.target.value }))}
                    className="cc-input-shell cc-input-field h-10 w-full rounded-xl px-3 text-sm outline-none"
                  >
                    {siteOptions.map((option) => (
                      <option key={`site-${option}`} value={option}>{option === 'all' ? 'Semua Site/Area' : option}</option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Paket">
                  <select
                    value={draftFilters.packageName}
                    onChange={(event) => setDraftFilters((prev) => ({ ...prev, packageName: event.target.value }))}
                    className="cc-input-shell cc-input-field h-10 w-full rounded-xl px-3 text-sm outline-none"
                  >
                    {packageOptions.map((option) => (
                      <option key={`package-${option}`} value={option}>{option === 'all' ? 'Semua Paket' : option}</option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Status Pengajuan">
                  <select
                    value={draftFilters.status}
                    onChange={(event) => setDraftFilters((prev) => ({ ...prev, status: event.target.value }))}
                    className="cc-input-shell cc-input-field h-10 w-full rounded-xl px-3 text-sm outline-none"
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={`status-${option.value}`} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Urgency / Priority">
                  <select
                    value={draftFilters.urgency}
                    onChange={(event) => setDraftFilters((prev) => ({ ...prev, urgency: event.target.value }))}
                    className="cc-input-shell cc-input-field h-10 w-full rounded-xl px-3 text-sm outline-none"
                  >
                    {URGENCY_OPTIONS.map((option) => (
                      <option key={`urgency-${option.value}`} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </FormField>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormField label="Tanggal Submit Dari">
                    <input
                      type="date"
                      value={draftFilters.dateFrom}
                      onChange={(event) => setDraftFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
                      className="cc-input-shell cc-input-field h-10 w-full rounded-xl px-3 text-sm outline-none"
                    />
                  </FormField>
                  <FormField label="Tanggal Submit Sampai">
                    <input
                      type="date"
                      value={draftFilters.dateTo}
                      onChange={(event) => setDraftFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
                      className="cc-input-shell cc-input-field h-10 w-full rounded-xl px-3 text-sm outline-none"
                    />
                  </FormField>
                </div>
              </div>
            </FormSection>

            <div className="mt-4 flex items-center justify-between gap-2">
              <UIButton variant="subtle" onClick={resetAdvancedFilters}>Reset Filter</UIButton>
              <UIButton onClick={applyAdvancedFilters}>Terapkan Filter</UIButton>
            </div>
          </aside>
        </div>
      ) : null}

      {selected ? (
        <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/65 p-3 md:items-center">
          <div className="cc-tonal-panel w-full max-w-6xl overflow-hidden">
            <div className="max-h-[86vh] overflow-y-auto p-4 md:p-5">
              <header className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <p className="cc-text-subtle text-[10px] font-semibold uppercase tracking-[0.18em]">Validation Decision Workspace</p>
                  <h2 className="cc-text-primary text-xl font-semibold">
                    #{String(selected?.request_code || '-')} · {String(selected?.full_name || '-')}
                  </h2>
                  <p className="cc-text-secondary mt-1 text-sm">
                    Submitted {formatDateTime(selected?.submitted_at || selected?.created_at)} · Teknisi: {resolveTechnicianValue(selected)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge tone={mapApplicationStatusToTone(selected?.status)}>{statusLabel(selected?.status)}</StatusBadge>
                  <UIButton variant="subtle" onClick={() => setSelected(null)} className="px-2.5 py-2">
                    <XCircle className="h-4 w-4" />
                  </UIButton>
                </div>
              </header>

              <div className="grid gap-4 lg:grid-cols-[1.75fr_1fr]">
                <div className="space-y-3">
                  <ReviewCard
                    title="Identitas Pengajuan"
                    rows={[
                      { label: 'Request', value: `#${String(selected?.request_code || '-')}` },
                      { label: 'Nama', value: String(selected?.full_name || '-') },
                      { label: 'Telepon', value: String(selected?.phone || '-') },
                      { label: 'Alamat', value: String(selected?.address || '-') },
                    ]}
                  />
                  <ReviewCard
                    title="Konteks Layanan"
                    rows={[
                      { label: 'Paket', value: String(selected?.package_name || selected?.package_id || '-') },
                      { label: 'PPPoE', value: String(selected?.pppoe_username || '-') },
                      { label: 'Site/NAS', value: `${String(selected?.site_id || '-')} / ${String(selected?.nas_id || '-')}` },
                      { label: 'OLT/ODP', value: `${String(selected?.olt_id || '-')} / ${String(selected?.odp_id || '-')}` },
                    ]}
                  />
                  <FormSection title="Evidence & Media" description="Validasi kelengkapan bukti instalasi pelanggan.">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <SectionContainer className="p-3">
                        <p className="cc-text-muted text-[10px] uppercase tracking-[0.14em]">Foto KTP</p>
                        {String(selected?.photo_ktp_url || selected?.photos_json?.[0] || '').trim() ? (
                          <a
                            href={String(selected?.photo_ktp_url || selected?.photos_json?.[0])}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 inline-flex text-xs text-cyan-200 underline"
                          >
                            Buka Foto KTP
                          </a>
                        ) : <p className="cc-text-secondary mt-1 text-xs">Belum ada foto KTP.</p>}
                      </SectionContainer>
                      <SectionContainer className="p-3">
                        <p className="cc-text-muted text-[10px] uppercase tracking-[0.14em]">Foto Rumah</p>
                        {String(selected?.photo_house_url || selected?.photos_json?.[1] || '').trim() ? (
                          <a
                            href={String(selected?.photo_house_url || selected?.photos_json?.[1])}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 inline-flex text-xs text-cyan-200 underline"
                          >
                            Buka Foto Rumah
                          </a>
                        ) : <p className="cc-text-secondary mt-1 text-xs">Belum ada foto rumah.</p>}
                      </SectionContainer>
                    </div>
                  </FormSection>
                </div>

                <div className="space-y-3">
                  <FormSection title="Decision Support" description="Ringkasan cepat untuk keputusan approve/revisi/reject.">
                    <div className="space-y-2">
                      {toDecisionChecklist(selected).map((item) => (
                        <div key={item.key} className="flex items-center justify-between rounded-lg bg-[#0c1524]/80 px-3 py-2">
                          <span className="cc-text-secondary text-xs">{item.label}</span>
                          <StatusBadge tone={item.ready ? 'ok' : 'warn'}>{item.ready ? 'Lengkap' : 'Perlu Cek'}</StatusBadge>
                        </div>
                      ))}
                    </div>
                  </FormSection>

                  <FormSection title="Notes & Instruction" description="Catatan internal admin untuk approval, revisi, atau penolakan.">
                    <FormField
                      label="Catatan Review"
                      helper="Wajib secara SOP untuk aksi Revisi/Tolak. Payload tetap menggunakan field review_notes."
                    >
                      <textarea
                        value={reviewNotes}
                        onChange={(event) => setReviewNotes(event.target.value)}
                        rows={6}
                        className="cc-input-shell cc-input-field w-full rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--cc-focus-ring)]"
                        placeholder="Isi alasan revisi/penolakan atau catatan approval."
                      />
                    </FormField>
                  </FormSection>
                </div>
              </div>
            </div>

            <StickyActionBar
              left={(
                <UIButton variant="subtle" onClick={() => setSelected(null)}>
                  <XCircle className="h-4 w-4" />
                  Tutup
                </UIButton>
              )}
              right={(
                <>
                  <UIButton
                    variant="subtle"
                    className="text-amber-200"
                    onClick={() => doReview('request_revision')}
                    disabled={Boolean(submittingAction)}
                  >
                    <ClipboardList className="h-4 w-4" />
                    {submittingAction === 'request_revision' ? 'Memproses...' : 'Minta Revisi'}
                  </UIButton>
                  <UIButton
                    variant="danger"
                    onClick={() => doReview('reject')}
                    disabled={Boolean(submittingAction)}
                  >
                    <XCircle className="h-4 w-4" />
                    {submittingAction === 'reject' ? 'Memproses...' : 'Tolak'}
                  </UIButton>
                  <UIButton onClick={() => doReview('approve')} disabled={Boolean(submittingAction)}>
                    <CheckCircle2 className="h-4 w-4" />
                    {submittingAction === 'approve' ? 'Memproses...' : 'Setujui & Buat Customer'}
                  </UIButton>
                </>
              )}
            />
          </div>
        </div>
      ) : null}
      </PageContainer>
    </AppShell>
  );
}



